import SessionVideoRecorderContext from 'shared/contexts/SessionVideoRecorderContext'
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import jsLogger from 'js-logger'
import SessionCanvasMixer from 'shared/components/recorder/SessionCanvasMixer'
import AudioMixer from 'shared/components/recorder/AudioMixer'
import RVideo from 'shared/components/recorder/Rvideo'
import { getBackClient } from 'shared/controllers/back'
import RecordingQualityController from 'shared/components/recorder/RecordingQualityController'

type Props = {
  children: ReactNode
  quality?: 'low' | 'medium' | 'high'
  avatarImageUrl?: string
  backgroundColor?: string
}

const SessionVideoRecorderDataProvider: FC<Props> = ({
  children,
  quality = 'high',
  avatarImageUrl,
  backgroundColor
}) => {
  const userVideoRef = useRef<HTMLVideoElement>()
  const avatarVideoRef = useRef<HTMLVideoElement>()
  const [userVideoReady, setUserVideoReady] = useState(false)
  const [userStream, setUserStream] = useState<MediaStream>()
  const [avatarStream, setAvatarStream] = useState<MediaStream>()
  const videoRecorderRef = useRef<RVideo>()
  const mixerRef = useRef<SessionCanvasMixer | null>(null)
  const audioMixerRef = useRef<AudioMixer | null>(null)
  const qualityControllerRef = useRef<RecordingQualityController | null>(null)
  const phase1StartedRef = useRef(false)

  useEffect(() => {
    // 🔴 FINALIZATION ON UNMOUNT
    return () => {
      jsLogger.log('SessionVideoRecorder: cleanup')

      videoRecorderRef.current?.stop()
      videoRecorderRef.current = undefined

      mixerRef.current?.stop()
      mixerRef.current = null

      audioMixerRef.current?.close()
      audioMixerRef.current = null

      userStream?.getTracks().forEach((t) => t.stop())
      avatarStream?.getTracks().forEach((t) => t.stop())

      qualityControllerRef.current = null
    }
  }, [])

  // Phase 1: start recording as soon as user stream + video element are ready
  useEffect(() => {
    if (phase1StartedRef.current) return
    if (!userStream || !userVideoReady || !userVideoRef.current) return

    phase1StartedRef.current = true

    const startRecording = async () => {
      jsLogger.log('SessionVideoRecorder: Phase 1 — starting early recording', {
        hasAvatarImageUrl: !!avatarImageUrl
      })

      const mixer = new SessionCanvasMixer(
        userVideoRef.current!,
        avatarImageUrl,
        quality,
        backgroundColor
      )

      const qualityController = new RecordingQualityController((q) => {
        jsLogger.log('SessionVideoRecorder: quality change', q)
        mixer.setQuality(q)
      })
      qualityControllerRef.current = qualityController

      mixer.start()
      mixerRef.current = mixer

      const audioMixer = new AudioMixer()
      await audioMixer.addStream(userStream, 'mic')
      audioMixerRef.current = audioMixer

      const canvasStream = mixer.getCanvas().captureStream()

      const finalStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...audioMixer.getStream().getAudioTracks()
      ])

      const bitrateMap = {
        high: 800_000,
        medium: 600_000,
        low: 400_000
      }

      videoRecorderRef.current = new RVideo(
        finalStream,
        (duration: number) => {
          jsLogger.log('SessionVideo: video recording is stopped', { duration })
        },
        (blob: Blob) => {
          const mimeType = videoRecorderRef.current?.mimeType || 'video/webm'
          const vBufferLength = getBackClient().sendVideoChunk(
            blob,
            mimeType,
            'avatar'
          )
          qualityControllerRef.current?.update(vBufferLength)
        },
        (er) => {
          jsLogger.error('SessionVideo: error:', { er })
        },
        false,
        bitrateMap[quality]
      )

      // Avatar stream may have arrived before Phase 1 completed (e.g. when
      // setClicked fires during a registration flow before the camera starts).
      // Apply it now so the canvas mixer draws the avatar video and audio is mixed.
      if (avatarVideoRef.current) {
        jsLogger.log(
          'SessionVideoRecorder: Phase 1 — applying pre-arrived avatar video'
        )
        mixer.setAvatarVideo(avatarVideoRef.current)
      }
      if (avatarStream) {
        jsLogger.log(
          'SessionVideoRecorder: Phase 1 — applying pre-arrived avatar audio'
        )
        audioMixer.addStream(avatarStream, 'avatar')
      }
    }

    startRecording()
  }, [userStream, userVideoReady, quality, avatarImageUrl, backgroundColor])

  // Re-add user audio when stream changes (e.g. after document scanning recaptures camera)
  useEffect(() => {
    if (!userStream || !audioMixerRef.current) return
    if (!phase1StartedRef.current) return // Phase 1 handles initial setup

    jsLogger.log('SessionVideoRecorder: user stream changed, re-adding audio')
    audioMixerRef.current.addStream(userStream, 'mic')
  }, [userStream])

  // Phase 2: when avatar stream arrives, plug it into the existing mixer + audio
  useEffect(() => {
    if (!avatarStream || !avatarVideoRef.current) return
    if (!mixerRef.current || !audioMixerRef.current) return

    jsLogger.log('SessionVideoRecorder: Phase 2 — avatar stream connected')

    mixerRef.current.setAvatarVideo(avatarVideoRef.current)
    audioMixerRef.current.addStream(avatarStream, 'avatar')
  }, [avatarStream])

  const setUserVideoElement = useCallback((v: HTMLVideoElement) => {
    userVideoRef.current = v
    setUserVideoReady(true)
  }, [])

  const setAvatarVideoElement = useCallback((v: HTMLVideoElement) => {
    avatarVideoRef.current = v
  }, [])

  return (
    <SessionVideoRecorderContext.Provider
      value={{
        setUserVideoElement,
        setAvatarVideoElement,
        setUserStream,
        setAvatarStream
      }}
    >
      {children}
    </SessionVideoRecorderContext.Provider>
  )
}

export default SessionVideoRecorderDataProvider
